/**
 * Produces a structured diff between two avail record versions.
 * Used by the avail comparison feature to highlight created, updated,
 * deleted, and unchanged fields in a side-by-side view.
 */
export class ValueComparer {
  VALUE_CREATED = 'created';

  VALUE_UPDATED = 'updated';

  VALUE_DELETED = 'deleted';

  VALUE_UNCHANGED = 'unchanged';

  /**
   * Recursively compares two avail version trees and returns a diff object
   * mirroring the original structure. Each leaf node becomes
   * `{valueStatus, data, oldData}` indicating how the value changed.
   * Object and array nodes are traversed recursively so nested changes
   * are captured at every depth level.
   *
   * @param {*} previous - The older avail version (or subtree).
   * @param {*} current  - The newer avail version (or subtree).
   * @returns {object} A diff tree where leaves are `{valueStatus, data, oldData}`.
   */
  compareAvailVersions = (previous, current) => {
    if (this.isFunction(previous) || this.isFunction(current)) {
      throw new Error('Invalid argument. Function given, object expected.');
    }
    if (this.isValue(previous) || this.isValue(current)) {
      return {
        valueStatus: this.compareValues(previous, current),
        data: current,
        oldData: previous,
      };
    }

    const diff = {};

    // Iterate previous keys to detect unchanged, updated, and deleted fields.
    for (const key in previous) {
      if (this.isFunction(previous[key])) {
        // eslint-disable-next-line no-continue
        continue;
      }

      let curValue;
      if (current[key] !== undefined) {
        curValue = current[key];
      }

      diff[key] = this.isArray(previous[key])
        ? previous[key].map((el, index) => this.compareAvailVersions(el, (curValue || [])[index]))
        : this.compareAvailVersions(previous[key], curValue);
    }
    // Iterate current keys to detect newly created fields not present in previous.
    for (const key in current) {
      if (this.isFunction(current[key]) || diff[key] !== undefined) {
        // eslint-disable-next-line no-continue
        continue;
      }

      diff[key] = this.isArray(current[key])
        ? current[key].map(el => this.compareAvailVersions(undefined, el))
        : this.compareAvailVersions(undefined, current[key]);
    }

    return diff;
  };

  /**
   * Determines the change status between two primitive values.
   * Returns one of VALUE_UNCHANGED, VALUE_CREATED, VALUE_DELETED, or VALUE_UPDATED
   * based on which side is undefined and whether the values are equal.
   */
  compareValues = (previous, current) => {
    if (previous === current) {
      return this.VALUE_UNCHANGED;
    }
    if (this.isDate(previous) && this.isDate(current) && previous.getTime() === current.getTime()) {
      return this.VALUE_UNCHANGED;
    }
    if (previous === undefined) {
      return this.VALUE_CREATED;
    }
    if (current === undefined) {
      return this.VALUE_DELETED;
    }
    return this.VALUE_UPDATED;
  };

  isFunction = x => Object.prototype.toString.call(x) === '[object Function]';

  isArray = x => Object.prototype.toString.call(x) === '[object Array]';

  isDate = x => Object.prototype.toString.call(x) === '[object Date]';

  isObject = x => Object.prototype.toString.call(x) === '[object Object]';

  isValue = x => !this.isObject(x) && !this.isArray(x);
}
